"use client";
import React, { ReactNode } from "react";
import { useIntl } from "react-intl";
import moment from "moment";

import { useJob } from "../_context/JobContext";

import Apply from "./Apply";
import { Skeleton } from "@components/ui/skeleton";

const Details = ({ role }: { role: string | undefined }) => {
  const { job } = useJob();

  const intl = useIntl();
  const translation: any = intl.messages["job-details"];

  return (
    <section
      id="job-details"
      className="py-5 px-6 sm:py-6 sm:px-7 border rounded-lg space-y-4 sm:space-y-6 scroll-mt-28"
    >
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-start justify-between">
        <h1 className="font-semibold text-xl text-neutral-900">{job.title}</h1>
        <p className="sm:pt-0.5 text-neutral-600 shrink-0 text-sm">
          Posted {moment(job?.createdAt).toNow(true)} ago
        </p>
      </div>
      <div className="border-b pb-4 sm:pb-6">
        <ul className="flex flex-wrap gap-2 max-w-2xl">
          <JobType />
          <Salary />
          <JobShift />
          <JobExperience />
          <JobLocation />
        </ul>
      </div>

      <JobSkills />
      <div className="flex items-center justify-between gap-5">
        {role === "CANDIDATE" ? (
          <div className="max-w-44 w-full hidden md:flex">
            <Apply />
          </div>
        ) : null}
        <ul className="flex divide-x gap-2 sm:gap-3 text-xs sm:text-sm text-neutral-600">
          <li>
            {job?.views?.toString() || "0"} {translation["viewed"]}
          </li>
          <li className="pl-2 sm:pl-3">
            {translation["open-positions"]} {job.positions}
          </li>
        </ul>
      </div>
    </section>
  );
};

export default Details;

export const Pill = ({ children }: { children: ReactNode }) => {
  if (!children) return null;
  return (
    <li className="py-1.5 px-4 rounded-full bg-gray-100 text-xs sm:text-sm">
      {children}
    </li>
  );
};

export const Salary = () => {
  const { job } = useJob();
  const intl = useIntl();
  const translation: any = intl.messages["job-details"];

  return !job?.hideSalary && (job?.salaryFrom || job?.salary) ? (
    <Pill>
      {translation["salary"]}:{" "}
      {job?.salaryFrom
        ? `${job?.salaryFrom || 0} ${job?.salaryTo ? `- ${job?.salaryTo}` : ""}`
        : job?.salary}{" "}
      {job?.salaryCurrency?.name}
    </Pill>
  ) : null;
};

export const JobShift = () => {
  const { job } = useJob();

  if (!job?.jobShift?.jobShift) return null;

  return <Pill>{job?.jobShift?.jobShift}</Pill>;
};

export const JobExperience = () => {
  const { job } = useJob();
  const intl = useIntl();
  const translation: any = intl.messages["job-details"];

  return job?.jobExperience?.jobExperience ? (
    <Pill>
      {translation["experience"]}: {job?.jobExperience?.jobExperience}
    </Pill>
  ) : null;
};

export const JobLocation = () => {
  const { job } = useJob();

  if (!job?.country?.country) return null;
  return (
    <Pill>
      {`${job?.city?.city ? job?.city?.city + ", " : ""}` || ""}{" "}
      {job?.country?.country || ""}
    </Pill>
  );
};

export const JobType = () => {
  const { job } = useJob();

  if (!job?.jobType?.jobType) return null;

  return <Pill>{job?.jobType?.jobType || ""}</Pill>;
};

export const JobSkills = () => {
  const { job } = useJob();

  const intl = useIntl();
  const translation: any = intl.messages["job-details"];

  return job?.jobSkills?.length ? (
    <div className="border-b pb-6 text-sm flex flex-wrap gap-1">
      <strong className="font-semibold text-neutral-700">
        {translation?.["needed-skills"]}
        {": "}
      </strong>
      {job.jobSkills.map((skill: any, index: number) => (
        <div key={index} className="whitespace-nowrap text-neutral-600">
          {index !== 0 ? " - " : null}
          {skill?.skill
            ? skill?.skill?.translations?.[0]?.jobSkill ||
              skill?.skill?.jobSkill
            : skill?.otherSkill?.otherSkill}
        </div>
      ))}
    </div>
  ) : null;
};

export const DetailsLoader = () => {
  return (
    <section
      id="job-details"
      className="py-5 px-6 sm:py-6 sm:px-7 border rounded-lg space-y-4 sm:space-y-6 scroll-mt-28"
    >
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-start justify-between">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="h-[22px] w-32 shrink-0" />
      </div>
      <div className="border-b pb-4 sm:pb-6">
        <ul className="flex flex-wrap gap-2 max-w-2xl">
          <Skeleton className="h-8 w-28 rounded-full" />
          <Skeleton className="h-8 w-28 rounded-full" />
          <Skeleton className="h-8 w-28 rounded-full" />
          <Skeleton className="h-8 w-28 rounded-full" />
          <Skeleton className="h-8 w-28 rounded-full" />
        </ul>
      </div>

      <div className="border-b pb-6 text-sm flex flex-wrap gap-1 whitespace-nowrap text-neutral-600">
        <Skeleton className="h-5 w-24" />:
        <Skeleton className="h-5 w-24" /> -
        <Skeleton className="h-5 w-24" /> -
        <Skeleton className="h-5 w-24" /> -
        <Skeleton className="h-5 w-24" />
      </div>

      <div className="flex items-center justify-between gap-5">
        <div className="max-w-44 w-full hidden md:flex">
          <Skeleton className="h-12 w-full rounded-md" />
        </div>
        <ul className="flex divide-x gap-2 sm:gap-3 text-xs sm:text-sm text-neutral-600">
          <li>
            <Skeleton className="h-5 w-24" />
          </li>
          <li className="pl-2 sm:pl-3">
            <Skeleton className="h-5 w-24" />
          </li>
        </ul>
      </div>
    </section>
  );
};
